import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import * as AccountApplicationsService from '../services/AccountApplicationsService';
import { AccountApplication } from '../types';
import { notifyError, notifySuccess } from '../../../components/PlatformNotification';

// Query keys
export const accountApplicationsKeys = {
    all: ['accountApplications'] as const,
    lists: () => [...accountApplicationsKeys.all, 'list'] as const,
    list: (filters: Record<string, string>) => [...accountApplicationsKeys.lists(), { filters }] as const,
    details: () => [...accountApplicationsKeys.all, 'detail'] as const,
    detail: (id: string) => [...accountApplicationsKeys.details(), id] as const,
};

const DEFAULT_LIMIT = 10;

// Hooks
//
// Token (cursor) pagination, the platform standard. `filters` carries the
// accountId this list is scoped to, and it is part of the queryKey — omit it
// and navigating between two accounts would serve the previous account's
// cached page.
export const useAccountApplicationsQuery = (filters: Record<string, string> = {}) => {
    return useInfiniteQuery({
        queryKey: accountApplicationsKeys.list(filters),
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            AccountApplicationsService.fetchRecords({
                filter: filters,
                limit: DEFAULT_LIMIT,
                nextToken: pageParam,
            }),
        // Returning null or the SAME token here refetches forever; undefined is
        // the only value that tells React Query the list is exhausted.
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null,
    });
};

export const useAccountApplicationQuery = (id: string) => {
    return useQuery({
        queryKey: accountApplicationsKeys.detail(id),
        queryFn: () => AccountApplicationsService.fetch(id),
        enabled: !!id,
    });
};

export const useCreateAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: AccountApplicationsService.add,
        onSuccess: () => {
            notifySuccess('Account application created');
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.lists() });
        },
        onError: notifyError,
    });
};

export const useUpdateAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, record }: { id: string; record: AccountApplication }) =>
            AccountApplicationsService.update(id, record),
        onSuccess: (_, { id }) => {
            notifySuccess('Account application updated');
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.lists() });
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.detail(id) });
        },
        onError: notifyError,
    });
};

export const useDeleteAccountApplicationMutation = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: AccountApplicationsService.deleteRecord,
        onSuccess: () => {
            notifySuccess('Account application deleted');
            queryClient.invalidateQueries({ queryKey: accountApplicationsKeys.all });
        },
        onError: notifyError,
    });
};

// Global filter state (shared across components)
let globalFilters: Record<string, string> = {};
let filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useAccountApplicationsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => {
            setFilters(newFilters);
        };
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return {
        filters,
        handleSetFilters,
        handleClearFilters,
    };
};
